import { getVenue, getVenueIndex } from '../../api/venueApi';
import { getVenueTagList } from '../../api/venueTagApi';
import { VenueFilterData, VenueIndexData } from '../../dto/EntityRepository';
import { useExhibitionVenueStore } from '../../store/exhibitionVenueStore';
import { getDefaultVenueSortData } from '../../util/venueListParams';
import {
  buildInterestExhibitionCards,
  getInterestedVenueIds,
} from '../../util/interestExhibitions';
import { useApiWrapper } from './apiWrapper';

let latestVenueListRequestId = 0;
let latestTagListRequestId = 0;

const normalizeVenueIndex = (venue: VenueIndexData): VenueIndexData => ({
  ...venue,
  id: String(venue.id),
  name: venue.name ?? '',
  nameEn: venue.nameEn ?? '',
});

const fetchAllVenues = async (filterData: VenueFilterData): Promise<VenueIndexData[]> => {
  const response = await getVenueIndex(getDefaultVenueSortData(), filterData);
  return (response.data.venues ?? []).map(normalizeVenueIndex);
};

export const useExhibitionVenue = () => {
  const store = useExhibitionVenueStore();
  const withApiHandler = useApiWrapper();

  const loadVenues = async (filterData = useExhibitionVenueStore.getState().filterData) => {
    const requestId = ++latestVenueListRequestId;

    await withApiHandler(async () => {
      const venues = await fetchAllVenues(filterData);
      if (requestId !== latestVenueListRequestId) return;
      store.setVenueList(venues);
    }, ['exhibition-venue-list', filterData], {}, false);
  };

  const loadTags = async () => {
    const requestId = ++latestTagListRequestId;
    await withApiHandler(async () => {
      try {
        const response = await getVenueTagList();
        if (requestId !== latestTagListRequestId) return;
        store.setTags(response.data);
      } catch (error) {
        if (requestId !== latestTagListRequestId) return;
        throw error;
      }
    }, ['exhibition-venue-tag-list'], {}, false);
  };

  const addInterestExhibitions = async (venueId: string): Promise<void> => {
    const normalizedVenueId = venueId.trim();
    if (!normalizedVenueId) return;

    const current = useExhibitionVenueStore.getState();
    const cached = current.venueExhibitionCache[normalizedVenueId];
    if (cached !== undefined) {
      current.addInterestExhibitions(cached);
      return;
    }
    if (current.loadingVenueIds.includes(normalizedVenueId)) return;

    current.setVenueLoading(normalizedVenueId, true);
    try {
      const response = await withApiHandler(
        () => getVenue(normalizedVenueId),
        ['exhibition-venue-detail', normalizedVenueId],
        {},
        false,
      );
      if (!response) return;

      const cards = buildInterestExhibitionCards(response.data);
      const latest = useExhibitionVenueStore.getState();
      latest.setVenueExhibitionCache(normalizedVenueId, cards);
      if (!latest.loadingVenueIds.includes(normalizedVenueId)) return;
      latest.addInterestExhibitions(cards);
    } finally {
      useExhibitionVenueStore.getState().setVenueLoading(normalizedVenueId, false);
    }
  };

  const setFilterData = (filterData: VenueFilterData) => {
    store.setFilterData(filterData);
    void loadVenues(filterData);
  };

  return {
    ...store,
    interestedVenueIds: getInterestedVenueIds(store.interestExhibitions),
    loadVenues,
    loadTags,
    addInterestExhibitions,
    setFilterData,
  };
};
